import Typography from '@material-ui/core/Typography';
import { GoogleLogin } from '@react-oauth/google';
import React, { FC, useEffect, useRef, useState } from 'react';

import useSize from '~/shared/hooks/useSize';
import { ProviderCredential } from '~/shared/services/userService';

import { generateNonce, sha256Hex } from '../utils/nonce';

type Props = {
  onCredential: (credential: ProviderCredential) => void;
  onError?: () => void;
  disabled?: boolean;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
};

const safeGenerateNonce = (): string | null => {
  try {
    return generateNonce();
  } catch {
    return null;
  }
};

// A fresh nonce per credential: the raw value goes to our API, its SHA-256 goes
// to Google so the ID token is bound to this attempt (Apple/Supabase convention).
// Both steps need Web Crypto (crypto.getRandomValues / crypto.subtle); the
// latter is missing on very old browsers and on non-secure (http://) origins.
// Rather than sending an unbound token, the button degrades to a hint and the
// email/password form stays available.
const useNonce = () => {
  const [nonce, setNonce] = useState(safeGenerateNonce);
  const [hashedNonce, setHashedNonce] = useState<string>();
  const [unsupported, setUnsupported] = useState(nonce === null);

  useEffect(() => {
    if (nonce === null) {
      setUnsupported(true);
      return undefined;
    }

    let cancelled = false;
    setHashedNonce(undefined);
    sha256Hex(nonce)
      .then(hash => {
        if (!cancelled) {
          setHashedNonce(hash);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnsupported(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return {
    nonce,
    hashedNonce,
    unsupported,
    rotate: () => setNonce(safeGenerateNonce()),
  };
};

const GoogleButton: FC<Props> = ({
  onCredential,
  onError,
  disabled = false,
  text = 'signin_with',
}) => {
  // GoogleLogin renders a fixed-width GSI iframe that would otherwise sit
  // left-aligned and narrow next to the full-width form buttons. Measure the
  // container and pass its width so the button stretches to match (GSI caps
  // width at 400px, which comfortably covers the fixed-width auth paper).
  // useSize goes through @react-hook/resize-observer's bundled ponyfill, so
  // this does not depend on a native ResizeObserver.
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useSize(containerRef);
  const width = size ? Math.floor(size.width) || undefined : undefined;
  const { nonce, hashedNonce, unsupported, rotate } = useNonce();

  return (
    <div
      ref={containerRef}
      style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
      aria-busy={disabled}
    >
      {unsupported && (
        <Typography variant="body2" color="textSecondary" align="center">
          {
            "Google sign-in isn't available in this browser. Please use your email and password."
          }
        </Typography>
      )}
      {!unsupported && hashedNonce && nonce && (
        <GoogleLogin
          text={text}
          width={width}
          nonce={hashedNonce}
          onSuccess={credentialResponse => {
            if (credentialResponse.credential) {
              onCredential({ idToken: credentialResponse.credential, nonce });
              rotate();
            }
          }}
          onError={onError}
        />
      )}
    </div>
  );
};

export default GoogleButton;
